import React from "react";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import { Toggle } from "../../components/Toggle";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  IconContainer,
  TitleRow,
  Divider,
  PreferenceRow,
  PreferenceInfo,
  PreferenceLabel,
  PreferenceDescription,
  PreferenceControl,
} from "./styles";
import { StyledSelect } from "../../components/Select";
import { useTheme } from "../../hooks";
import { PreferencesFormProps } from "./types";

const LANGUAGE_OPTION = { label: "English", value: "en" };

const PreferencesFormComponent: React.FC<PreferencesFormProps> = ({
  onUpdate,
  userPreferences,
}) => {
  const [preferences, setPreferences] = React.useState(userPreferences);
  const { onToggleTheme, isDarkTheme } = useTheme();

  const handlePreferenceChange = React.useCallback(
    async <K extends keyof typeof preferences>(
      key: K,
      value: (typeof preferences)[K],
    ) => {
      setPreferences((prev) => ({
        ...prev,
        [key]: value,
      }));

      await onUpdate({ [key]: value });
    },
    [onUpdate],
  );

  return (
    <Card className="preferences">
      <CardHeader>
        <TitleRow>
          <IconContainer>
            <TuneOutlined />
          </IconContainer>
          <CardTitle>Preferences</CardTitle>
        </TitleRow>
        <CardDescription>
          Customize your experience and how you interact with the platform.
        </CardDescription>
      </CardHeader>

      <Toggle
        testId="email-notifications-toggle"
        checked={preferences.emailNotifications}
        onChange={(value) =>
          handlePreferenceChange("emailNotifications", value)
        }
        label="Email notifications"
        description="Receive updates and alerts about transactions, security and account activity."
      />

      <Divider />

      <Toggle
        testId="newsletter-consent-toggle"
        checked={preferences.newsletterConsent}
        onChange={(value) => handlePreferenceChange("newsletterConsent", value)}
        label="Newsletter"
        description="Receive occasional financial tips and product updates. You can opt out at any time."
      />

      <Divider />

      <Toggle
        testId="theme-mode-toggle"
        checked={isDarkTheme}
        onChange={onToggleTheme}
        label="Dark mode"
        description="Switch between light and dark themes. Reduces eye strain in low light."
      />

      <Divider />

      <PreferenceRow>
        <PreferenceInfo>
          <PreferenceLabel>Language</PreferenceLabel>
          <PreferenceDescription>
            More languages coming soon. English is currently the only supported
            language.
          </PreferenceDescription>
        </PreferenceInfo>
        <PreferenceControl>
          <StyledSelect
            options={[LANGUAGE_OPTION]}
            // Select is non-interactive; no-op handler provided intentionally.
            handleChange={() => {}}
            value={LANGUAGE_OPTION}
            ariaLabel="Language"
          />
        </PreferenceControl>
      </PreferenceRow>
    </Card>
  );
};

export const PreferencesForm = React.memo(PreferencesFormComponent);
